import { createSlice } from "@reduxjs/toolkit";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { client } from "..";
import {
  GET_SHARED_EMAILS,
  ADD_SHARED_EMAIL,
  EDIT_SHARED_EMAIL,
  DELETE_SHARED_EMAIL,
} from "../Queries/SharedEmailQuerries";
import { UPDATE_EMP_SHARED_EMAIL_SETTINGS } from "../Queries/Emails";
export const fetchEmails = createAsyncThunk(
  "sharedEmail/fetchEmails",
  async (_, thunkAPI) => {
    try {
      const { data } = await client.query({
        query: GET_SHARED_EMAILS,
        fetchPolicy: "network-only",
      });
      return data.getAllSharedEmails;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// ✅ Add Email
export const addEmail = createAsyncThunk(
  "sharedEmail/addEmail",
  async (email, thunkAPI) => {
    try {
      const { data } = await client.mutate({
        mutation: ADD_SHARED_EMAIL,
        variables: { email },
      });

      if (data.createSharedEmail.status === 200) {
        return { email: data.createSharedEmail };
      }

      return thunkAPI.rejectWithValue(data.createSharedEmail.message);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// ✅ Edit Email
export const editEmail = createAsyncThunk(
  "sharedEmail/editEmail",
  async ({ id, email, status }, thunkAPI) => {
    try {
      const { data } = await client.mutate({
        mutation: EDIT_SHARED_EMAIL,
        variables: { id, email, status },
      });

      if (data.updateSharedEmail.status === 200) {
        return data.updateSharedEmail;
      }

      return thunkAPI.rejectWithValue(data.updateSharedEmail.message);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// ✅ Delete Email
export const deleteEmail = createAsyncThunk(
  "sharedEmail/deleteEmail",
  async ({ id, email }, thunkAPI) => {
    try {
      const { data } = await client.mutate({
        mutation: DELETE_SHARED_EMAIL,
        variables: { id, email },
      });

      if (data.deleteSharedEmail.status === 200) {
        return id; // return the id to remove from state
      }

      return thunkAPI.rejectWithValue(data.deleteSharedEmail.message);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateEmpSharedEmailSettings = createAsyncThunk(
  "sharedEmail/updateEmpSharedEmailSettings",
  async ({ employeeId, sharedemailSettings }, thunkAPI) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_EMP_SHARED_EMAIL_SETTINGS,
        variables: {
          employeeId,
          sharedemailSettings,
        },
      });
      const result = data?.UpdateEmpSharedEmailSettings;

      if (result?.success) {
        return result;
      }

      return thunkAPI.rejectWithValue(
        result?.message || "Failed to update email settings."
      );
    } catch (error) {
      console.error("Mutation Error:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const initialState = {
  sharedemails: [],
  usersharedemails: [],
  loading: false,
  error: null,
};

const sharedEmailSlice = createSlice({
  name: "sharedEmail",
  initialState,
  reducers: {
    clearEmailError: (state) => {
      state.error = null;
    },
    usersharedemaillist: (state, action) => {
      state.usersharedemails = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // ✅ Fetch
      .addCase(fetchEmails.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEmails.fulfilled, (state, action) => {
        state.loading = false;
        state.sharedemails = action.payload;
      })
      .addCase(fetchEmails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Add
      .addCase(addEmail.pending, (state) => {
        state.loading = true;
      })
      .addCase(addEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.sharedemails = [
          ...state.sharedemails,
          ...action.payload.email.emails,
        ];
      })
      .addCase(addEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Edit
      .addCase(editEmail.pending, (state) => {
        state.loading = true;
      })
      .addCase(editEmail.fulfilled, (state, action) => {
        state.loading = false;
        const updatedEmail = action.payload.emails[0]; // Extract updated email object

        const index = state.sharedemails.findIndex(
          (e) => e._id === updatedEmail._id
        );
        if (index !== -1) {
          state.sharedemails[index] = updatedEmail;
        }
      })

      .addCase(editEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Delete
      .addCase(deleteEmail.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.sharedemails = state.sharedemails.filter(
          (e) => e._id !== action.payload
        );
      })
      .addCase(deleteEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateEmpSharedEmailSettings.fulfilled, (state, action) => {
        state.loading = false;

        const settingsResponse = action.payload.response?.[0]?.sharedemailSettings || [];

        // Update usersharedemails
        state.usersharedemails = settingsResponse;

        // Optionally, update sharedemails list by matching _id with email ID
        settingsResponse.forEach((updatedSetting) => {
          const index = state.sharedemails.findIndex(
            (e) => e._id === updatedSetting.email
          );
          if (index !== -1) {
            state.sharedemails[index].status = updatedSetting.status;
          }
        });
      })

  },
});

export const { clearEmailError, usersharedemaillist, } =
  sharedEmailSlice.actions;
export default sharedEmailSlice.reducer;
